/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Select, SelectProps, MenuItem, SelectChangeEvent } from '@mui/material';
import { FC, ReactNode } from 'react';
import { KeyboardArrowDown } from '@mui/icons-material';

import { uuid } from '../../../util/uuid';
import FormControlMessage from '../FormControlMessage/FormControlMessage';
import FormControlLabel, { TLabelColor } from '../FormControlLabel/FormControlLabel';
import TestIds from '../../../testIds';
import { StyledFormControl } from '../StyledFormControl';

import styles from './SelectFormControl.module.scss';

export interface ISelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface ISelectFormControlProps extends Omit<SelectProps, 'onChange'> {
  'data-testid'?: string;
  hint?: string;
  errorMessage?: string;
  maxWidth?: string | number;
  options: ISelectOption[];
  onChange?: (event: SelectChangeEvent<unknown>, child: ReactNode) => void;
  labelColor?: TLabelColor;
}

const SelectFormControl: FC<ISelectFormControlProps> = ({
  id = uuid(),
  label,
  hint,
  error,
  errorMessage,
  required,
  disabled,
  maxWidth,
  options,
  'data-testid': testId,
  onChange,
  placeholder,
  variant = 'outlined',
  color = 'primary',
  className = '',
  labelColor = 'primary',
  inputProps,
  ...selectProps
}: ISelectFormControlProps): JSX.Element => (
  <StyledFormControl
    data-testid={TestIds.components.formControls.selectFormControl.component(testId)}
    className={`${className} ${styles.flSelectFormControl}`.trim()}
    maxWidth={maxWidth}
  >
    {label && (
      <FormControlLabel
        data-testid={TestIds.components.formControls.selectFormControl.label}
        htmlFor={id}
        required={required}
        color={labelColor}
      >
        {label}
      </FormControlLabel>
    )}

    <Select
      data-testid={TestIds.components.formControls.selectFormControl.input}
      error={error}
      required={required}
      disabled={disabled}
      displayEmpty
      onChange={onChange}
      variant={variant}
      color={color}
      IconComponent={KeyboardArrowDown}
      className={!selectProps?.value ? styles.flSelectFormControl__placeholder : undefined}
      inputProps={{
        id: id,
        ...inputProps,
      }}
      {...selectProps}
    >
      {placeholder && (
        <MenuItem value='' disabled>
          {placeholder}
        </MenuItem>
      )}
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </MenuItem>
      ))}
    </Select>

    <FormControlMessage
      data-testid={TestIds.components.formControls.selectFormControl.message}
      error={error}
      errorMessage={
        disabled && error ? 'Invalid input. Please contact Farmlands to update.' : errorMessage
      }
      hint={hint}
    />
  </StyledFormControl>
);

export default SelectFormControl;
